/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useState } from 'react';

import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import { I18n } from '@coze-arch/i18n';
import {
  Button,
  Input,
  Modal,
  Space,
  Tag,
  Toast,
  type ColumnProps,
} from '@coze-arch/coze-design';
import { Popconfirm, Typography, UIEmpty } from '@coze-arch/bot-semi';
import { type OAuthApp } from '@coze-arch/bot-api/oauth_permission_api';

import { AuthTable } from '@/components/auth-table';
import {
  useCreateOAuthApp,
  useDeleteOAuthApp,
  useDisableOAuthApp,
  useGetOAuthAppList,
} from '@/hooks/oauth/use-oauth';

export const OAuthBody: React.FC<{ spaceId: string; isOwner?: boolean }> = ({
  spaceId,
  isOwner = false,
}) => {
  const { dataSource, loading, fetchData } = useGetOAuthAppList({ spaceId });
  const { runCreate, loading: createLoading, successData } = useCreateOAuthApp();
  const { runDisable } = useDisableOAuthApp({ successHandle: fetchData });
  const { runDelete } = useDeleteOAuthApp({ successHandle: fetchData });

  const [showCreate, setShowCreate] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    fetchData();
  }, [spaceId]);

  const handleCreate = async () => {
    await runCreate({ name, description, space_id: spaceId });
    setShowCreate(false);
    setShowResult(true);
  };

  const handleCopyAll = () => {
    const content = [
      `client_id: ${successData?.client_id ?? ''}`,
      `public_key_id: ${successData?.public_key_id ?? ''}`,
      `private_key: ${successData?.private_key ?? ''}`,
    ].join('\n');
    copy(content);
    Toast.success({ content: I18n.t('oauth_copied') });
  };

  const columns: ColumnProps<OAuthApp>[] = [
    { title: I18n.t('name'), dataIndex: 'name', key: 'name' },
    { title: 'client_id', dataIndex: 'client_id', key: 'client_id' },
    {
      title: 'public_key_id',
      dataIndex: 'public_key_id',
      key: 'public_key_id',
    },
    {
      title: I18n.t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (_, record) =>
        record.status === 1 ? (
          <Tag color="green">{I18n.t('oauth_enabled')}</Tag>
        ) : (
          <Tag color="grey">{I18n.t('oauth_disabled')}</Tag>
        ),
    },
    {
      title: I18n.t('create_time'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_, record) =>
        record.created_at
          ? dayjs.unix(Number(record.created_at)).format('YYYY-MM-DD HH:mm')
          : '-',
    },
    {
      title: I18n.t('operation'),
      key: 'op',
      render: (_, record) => (
        <Space>
          {record.status === 1 ? (
            <Popconfirm
              title={I18n.t('oauth_disable_title')}
              content={I18n.t('oauth_disable_content')}
              okText={I18n.t('confirm')}
              cancelText={I18n.t('cancel')}
              onConfirm={() => runDisable(String(record.id))}
            >
              <Button size="small">{I18n.t('disable')}</Button>
            </Popconfirm>
          ) : null}
          <Popconfirm
            title={I18n.t('oauth_delete_title')}
            content={I18n.t('oauth_delete_content')}
            okText={I18n.t('confirm')}
            cancelText={I18n.t('cancel')}
            okType="danger"
            onConfirm={() => runDelete(String(record.id))}
          >
            <Button size="small" type="danger">
              {I18n.t('delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full h-full flex flex-col">
      <Space className="w-full justify-between mb-4">
        <h3 className="m-0">{I18n.t('oauth_app')}</h3>
        {isOwner ? (
          <Button
            theme="solid"
            type="primary"
            onClick={() => setShowCreate(true)}
          >
            {I18n.t('oauth_app_create')}
          </Button>
        ) : null}
      </Space>

      <AuthTable
        tableProps={{
          rowKey: 'id',
          loading,
          dataSource,
          columns,
        }}
        empty={
          <UIEmpty
            empty={{
              title: I18n.t('oauth_app_no_data'),
              description: I18n.t('oauth_app_empty_desc'),
              ...(isOwner
                ? {
                    btnText: I18n.t('oauth_app_create'),
                    btnOnClick: () => setShowCreate(true),
                  }
                : {}),
            }}
          />
        }
      />

      <Modal
        title={I18n.t('oauth_app_create')}
        visible={showCreate}
        confirmLoading={createLoading}
        onOk={handleCreate}
        onCancel={() => setShowCreate(false)}
        okText={I18n.t('confirm')}
      >
        <Space vertical className="w-full">
          <Input
            placeholder={I18n.t('oauth_app_name')}
            value={name}
            onChange={setName}
          />
          <Input
            placeholder={I18n.t('oauth_app_description')}
            value={description}
            onChange={setDescription}
          />
        </Space>
      </Modal>

      <Modal
        title={I18n.t('oauth_app_created')}
        visible={showResult}
        footer={null}
        onCancel={() => setShowResult(false)}
      >
        <Space vertical className="w-full">
          <Typography.Paragraph type="warning" className="!mb-0">
            {I18n.t('oauth_private_key_once')}
          </Typography.Paragraph>
          <div>
            <Typography.Paragraph ellipsis={{ rows: 2 }}>
              client_id: {successData?.client_id}
            </Typography.Paragraph>
          </div>
          <div>
            <Typography.Paragraph ellipsis={{ rows: 2 }}>
              public_key_id: {successData?.public_key_id}
            </Typography.Paragraph>
          </div>
          <div>
            <Typography.Paragraph ellipsis={{ rows: 4 }}>
              private_key: {successData?.private_key}
            </Typography.Paragraph>
          </div>
          <Button theme="solid" type="primary" onClick={handleCopyAll}>
            {I18n.t('oauth_copy_all')}
          </Button>
        </Space>
      </Modal>
    </div>
  );
};
